import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Copy, 
  Check, 
  Building2, 
  ShieldCheck, 
  Truck, 
  Calendar, 
  DollarSign, 
  CreditCard, 
  Lock, 
  Sparkles,
  Award,
  Landmark,
  Sliders,
  Plus,
  Trash2,
  Bookmark,
  RefreshCw,
  FolderOpen,
  ArrowRight,
  MoveHorizontal,
  ChevronDown,
  ChevronUp,
  Settings2,
  Save
} from 'lucide-react';
import { BusinessSettings } from '../types';
import { parseBankDetails, formatBankDetails, saveBusinessSettings } from '../utils/businessSettings';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

export interface DeliverableLineItem {
  id: string;
  title: string;
  description: string;
  duration: string;
  amount: number;
}

export interface EnterpriseProspect {
  id: string;
  name: string;
  category: 'logistics' | 'accelerator' | 'enterprise' | 'mining' | 'custom';
  clientCompany: string;
  clientExecutive: string;
  clientEmail: string;
  targetDivision: string;
  targetCorridors: string;
  operationalFocus: string;
  sprintFeeExVat: number;
  annualContractTarget: string;
  proformaNumber: string;
  issueDate: string;
  dueDate: string;
  recitalsCustomText?: string;
  specialNotes?: string;
  deliverables: DeliverableLineItem[];
  status?: 'active' | 'cancelled' | 'paid';
}

const DEFAULT_PROSPECTS: EnterpriseProspect[] = [
  {
    id: 'prospect_zibuke_sadc',
    name: '🤝 Zibuke Africa x SADC Mining & Logistics Portfolio Pilot',
    category: 'custom',
    clientCompany: 'Zibuke Africa / Designated Portfolio Fleet Operator',
    clientExecutive: 'Neolin Mbombi, Managing Director',
    clientEmail: 'connect@zibuke.africa',
    targetDivision: 'Cross-Border Mining & Logistics Operations',
    targetCorridors: 'Gauteng – Beitbridge, Durban – Harare, Kolwezi / DRC Copperbelt',
    operationalFocus: 'Route Margin Defense, Multi-Currency Volatility (USD/ZAR/BWP), SEFA/Statutory Audit Packaging, and Executive Co-Authored Case Study Synthesis',
    sprintFeeExVat: 30000,
    annualContractTarget: 'R150,000 – R240,000 (ex VAT)',
    proformaNumber: 'AZ-PILOT-ZIB-2026',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 5);
      return d.toISOString().split('T')[0];
    })(),
    specialNotes: 'Includes dedicated Google Cloud tenant provisioning, custom ledger data mapping, weekly founder advisory checkpoints, and executive co-authored SADC case study synthesis. Non-refundable pilot commitment; 100% credited against annual enterprise rollout.',
    deliverables: [
      {
        id: 'del_z1',
        title: 'Dedicated Google Cloud Sandbox Tenant & Security Architecture',
        description: 'Provisioning dedicated sovereign cloud tenant on Google Cloud with AES-256-GCM zero-custody encryption and role-based access for operating fleet company.',
        duration: '30 Days',
        amount: 10000
      },
      {
        id: 'del_z2',
        title: 'Cross-Border SADC Corridor Margin Defense & FX Volatility Engine',
        description: 'Multi-currency ledger mapping (ZAR, USD, BWP), route fuel slip reconciliation, cross-border withholding tax ring-fencing, and trailing 30–60 day receivables aging.',
        duration: 'Included',
        amount: 8000
      },
      {
        id: 'del_z3',
        title: 'SEFA & Statutory Tax Solvency Compliance Audit Pack Hub',
        description: 'Automated SARS 15% VAT calculation, provisional tax buffer isolation, and 1-click cryptographic CPA & SEFA compliance audit export engine.',
        duration: 'Included',
        amount: 5000
      },
      {
        id: 'del_z4',
        title: 'Executive Co-Authored SADC Benchmark Case Study & Advisory Checkpoints',
        description: 'Four (4) weekly 45-min strategic checkpoints plus formal synthesis of operational proof points, margin metrics, and co-authored case study publication.',
        duration: '4 Sessions + Synthesis',
        amount: 7000
      }
    ]
  },
  {
    id: 'prospect_imperial_logistics',
    name: '🚚 Imperial Logistics / B2B Freight Group',
    category: 'logistics',
    clientCompany: 'Imperial Logistics & Freight Holdings',
    clientExecutive: 'Group Chief Executive Officer',
    clientEmail: 'executive.office@imperialholdings.co.za',
    targetDivision: 'Cross-Border Freight & Fleet Logistics',
    targetCorridors: 'JHB – Gaborone, Durban – Harare, Beitbridge Corridor',
    operationalFocus: 'Route Fuel Variance Tracking, 30–60 Day Trailing Invoice Reconciliation, Multi-Currency Disbursements & Solvency Ring-Fencing',
    sprintFeeExVat: 15000,
    annualContractTarget: 'R96,000 – R120,000 (ex VAT)',
    proformaNumber: 'AZ-PILOT-2026-001',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    })(),
    specialNotes: 'Remote guided onboarding with weekly review calls. If on-site session at Johannesburg HQ is requested, travel is billed as direct passthrough.',
    deliverables: [
      {
        id: 'del_1',
        title: '30-Day Guided Enterprise Sandbox Pilot Provisioning',
        description: 'Dedicated tenant provisioning on AzuraOS local-first architecture for Cross-Border Freight & Fleet Logistics. Full AES-256 client-side zero-custody encryption.',
        duration: '30 Days',
        amount: 6000
      },
      {
        id: 'del_2',
        title: 'Route Margin Defense & Fuel Volatility Engine Configuration',
        description: 'Corridor stream tagging (JHB – Gaborone, Durban – Harare), trip fuel slip reconciliations, and trailing 30–60 day client billing aging matrix.',
        duration: 'Included',
        amount: 3750
      },
      {
        id: 'del_3',
        title: 'Statutory Tax Solvency Ring-Fencing & CPA Audit Hub',
        description: 'SARS 15% VAT calculation, provisional tax buffers, and 1-click cryptographic CPA/SEFA compliance CSV and PDF export generator.',
        duration: 'Included',
        amount: 2250
      },
      {
        id: 'del_4',
        title: 'High-Touch Advisory: Four (4) Weekly Executive Checkpoints',
        description: 'Scheduled weekly 45-minute strategy reviews via Google Meet with AzuraOS Enterprise Architect to assess variance and optimize workflow.',
        duration: '4 Sessions',
        amount: 3000
      }
    ]
  },
  {
    id: 'prospect_g4s_accelerator',
    name: '🚀 Google for Startups Accelerator / Founder Cohort',
    category: 'accelerator',
    clientCompany: 'Google for Startups Accelerator Africa Cohort',
    clientExecutive: 'Head of Startup Ecosystem & Program Director',
    clientEmail: 'startups-accelerator@google.com',
    targetDivision: 'Pre-Seed & Seed Founder Portfolio Operations',
    targetCorridors: 'Pan-African Founder Network (SA, Nigeria, Kenya, Ghana)',
    operationalFocus: '12-Month Founder Perk / Pro Access Pilot, Runway Health Tracking, Multi-Currency Invoicing & Cloud Credit Optimization',
    sprintFeeExVat: 12000,
    annualContractTarget: 'R80,000 – R120,000 (ex VAT)',
    proformaNumber: 'AZ-PILOT-G4S-2026',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    })(),
    specialNotes: 'Includes customized cohort onboarding webinar, 12-month founder perk integration, and real-time runway variance tracking for accelerator directors.',
    deliverables: [
      {
        id: 'del_g1',
        title: 'Turnkey Cohort Sandbox Deployment & Founder Onboarding',
        description: 'Private multi-tenant environment setup for 10–30 accelerator cohort founders with zero-knowledge AES-256 client vault.',
        duration: '30 Days',
        amount: 4800
      },
      {
        id: 'del_g2',
        title: 'Runway Health Engine & Multi-Currency Billing Matrix',
        description: 'Autonomous runway burn calculations, cross-border client invoicing (ZAR/USD/EUR/NGN/KES), and deposit milestone automation.',
        duration: 'Included',
        amount: 3000
      },
      {
        id: 'del_g3',
        title: 'Investor-Ready SEFA & CPA Audit Compliance Hub',
        description: 'Cryptographic compliance audit packs, provisional tax buffer ring-fencing, and 1-click CSV/PDF exports for board reporting.',
        duration: 'Included',
        amount: 1800
      },
      {
        id: 'del_g4',
        title: 'Four (4) Weekly Cohort CFO Advisory Checkpoints',
        description: 'Direct 45-minute weekly cohort strategy workshops on cashflow defense, prompt receivables recovery, and venture solvency.',
        duration: '4 Sessions',
        amount: 2400
      }
    ]
  },
  {
    id: 'prospect_esd_supply_chain',
    name: '🏢 Corporate ESD & B2B Supply Chain Enterprise',
    category: 'enterprise',
    clientCompany: 'Enterprise Supplier Development (ESD) Beneficiaries',
    clientExecutive: 'Head of Supplier Development & Procurement',
    clientEmail: 'esd-procurement@corporategroup.co.za',
    targetDivision: 'Strategic Vendor & Contractor Operations',
    targetCorridors: 'Gauteng, Western Cape & KZN Regional Hubs',
    operationalFocus: 'Supplier Solvency Ring-Fencing, 30-Day Prompt Payment Compliance, Automated SARS VAT Buffering & Funder Oversight',
    sprintFeeExVat: 15000,
    annualContractTarget: 'R120,000 – R150,000 (ex VAT)',
    proformaNumber: 'AZ-PILOT-ESD-2026',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    })(),
    specialNotes: 'Tailored for corporate ESD sponsors requiring verified financial solvency of small suppliers before releasing contract purchase orders.',
    deliverables: [
      {
        id: 'del_e1',
        title: 'Supplier Governance Sandbox Provisioning',
        description: 'Dedicated isolated tenant setups for contractor suppliers with AES-256 local ledger defense and zero cloud vendor lock-in.',
        duration: '30 Days',
        amount: 6000
      },
      {
        id: 'del_e2',
        title: 'Cashflow Ring-Fencing & Margin Variance Automation',
        description: 'Automatic isolation of statutory tax buffers (SARS 15% VAT & provisional tax) before vendor cash is redeployed into payroll.',
        duration: 'Included',
        amount: 3750
      },
      {
        id: 'del_e3',
        title: 'ESD Funder Audit Pack & Verification Checksums',
        description: 'Generation of audit-ready compliance packs with SHA-256 cryptographic verification for corporate sponsor compliance.',
        duration: 'Included',
        amount: 2250
      },
      {
        id: 'del_e4',
        title: 'Four (4) Executive Weekly Checkpoints & Governance Reviews',
        description: 'Weekly advisory sessions reviewing invoice aging, eliminating contractor payment friction, and ensuring prompt settlement.',
        duration: '4 Sessions',
        amount: 3000
      }
    ]
  },
  {
    id: 'prospect_dealership_pilot',
    name: '🚗 Eagle Ford Motor Company (14-Day Dealership Pilot)',
    category: 'custom',
    clientCompany: 'Eagle Ford Motor Company',
    clientExecutive: 'Karisma Singh-Jailall (Financial Manager) & Craig Gillesen (Dealer Principal)',
    clientEmail: 'karisma@eagleford.co.za',
    targetDivision: 'Dealership Vehicle Sales, Inhouse/Bank Finance & Treasury Ops',
    targetCorridors: 'Johannesburg Showrooms & Regional Vehicle Corridors',
    operationalFocus: '0.05s Multi-Channel Bank Reconciliation (Cash, EFT, WesBank/Absa/MFC Payouts), Floorplan Interest Defense, and SARS 15% VAT Reserve Ring-Fencing',
    sprintFeeExVat: 0,
    annualContractTarget: 'R36,000 – R60,000 (R3,000 – R5,000/mo vs R25,000/mo Assistant Accountant)',
    proformaNumber: 'AZ-PILOT-EAGLEFORD-2026',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 14);
      return d.toISOString().split('T')[0];
    })(),
    specialNotes: 'Complimentary 14-day internal parallel run for Eagle Ford Motor Company. 100% air-gapped local-first architecture by Azura Seraph™ Pty Ltd (Reg: 2024/560516/07). Zero online banking password sharing.',
    deliverables: [
      {
        id: 'del_auto_1',
        title: '14-Day Air-Gapped Dealership Sandbox Provisioning',
        description: 'Dedicated client-side vault setup running on local office hardware with AES-256-GCM encryption. Zero password sharing; no Plaid/Yodlee liabilities.',
        duration: '14 Days',
        amount: 0
      },
      {
        id: 'del_auto_2',
        title: 'Multi-Channel Bank Reconciliation Engine (Cash, EFT & Vehicle Asset Finance)',
        description: 'Sub-50ms parsing of Nedbank, FNB, Standard Bank, and Absa statements. Automatically matches vehicle stock numbers, customer cash deposits, and bank finance payouts (WesBank, MFC, Absa).',
        duration: 'Included',
        amount: 0
      },
      {
        id: 'del_auto_3',
        title: 'SARS 15% VAT Holding Shield & Floorplan Interest Margin Defense',
        description: 'Calculates indicative 15% VAT holding buffers on second-hand vehicle gross profits and provisional tax reserves, protecting the dealership from unexpected bi-monthly VAT201 shortfalls.',
        duration: 'Included',
        amount: 0
      },
      {
        id: 'del_auto_4',
        title: '1-Click Human-in-the-Loop Verification & Executive Cash Flow Reporting',
        description: 'Empowers the Financial Manager to review, verify, and approve reconciliations with a single click, eliminating 15+ hours/week of manual data entry without adding headcount.',
        duration: 'Included',
        amount: 0
      }
    ]
  }
];

interface EnterprisePilotGeneratorProps {
  businessSettings: BusinessSettings;
  showToast: (msg: string) => void;
  currentUserEmail?: string | null;
}

function EnterprisePilotGeneratorComponent({
  businessSettings,
  showToast,
  currentUserEmail,
}: EnterprisePilotGeneratorProps) {
  const [activeTab, setActiveTab] = useState<'proforma' | 'agreement' | 'pitch'>('proforma');
  const [copiedInvoiceText, setCopiedInvoiceText] = useState(false);
  const [copiedAgreementText, setCopiedAgreementText] = useState(false);
  const [copiedPitchText, setCopiedPitchText] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [documentTheme, setDocumentTheme] = useState<'dark' | 'light'>('dark');

  // Mobile Horizontal Scroll & Zoom Scale State
  const [previewScale, setPreviewScale] = useState<number>(1.0);

  // Prospect Profiles State (Local Storage Persistence)
  const [savedProspects, setSavedProspects] = useState<EnterpriseProspect[]>(() => {
    try {
      const local = localStorage.getItem('azuraos_enterprise_prospects');
      if (local) {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const hasDealership = parsed.some((p: any) => p.id === 'prospect_dealership_pilot');
          if (!hasDealership) {
            const dealership = DEFAULT_PROSPECTS.find(p => p.id === 'prospect_dealership_pilot');
            if (dealership) {
              return [dealership, ...parsed];
            }
          }
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse saved prospects:', e);
    }
    return DEFAULT_PROSPECTS;
  });

  const [activeProspectId, setActiveProspectId] = useState<string>(DEFAULT_PROSPECTS[0].id);

  // Form State for the Current Active Prospect
  const [clientCompany, setClientCompany] = useState(DEFAULT_PROSPECTS[0].clientCompany);
  const [clientExecutive, setClientExecutive] = useState(DEFAULT_PROSPECTS[0].clientExecutive);
  const [clientEmail, setClientEmail] = useState(DEFAULT_PROSPECTS[0].clientEmail);
  const [targetDivision, setTargetDivision] = useState(DEFAULT_PROSPECTS[0].targetDivision);
  const [targetCorridors, setTargetCorridors] = useState(DEFAULT_PROSPECTS[0].targetCorridors);
  const [operationalFocus, setOperationalFocus] = useState(DEFAULT_PROSPECTS[0].operationalFocus);
  const [annualContractTarget, setAnnualContractTarget] = useState(DEFAULT_PROSPECTS[0].annualContractTarget);
  const [proformaNumber, setProformaNumber] = useState(DEFAULT_PROSPECTS[0].proformaNumber);
  const [issueDate, setIssueDate] = useState(DEFAULT_PROSPECTS[0].issueDate);
  const [dueDate, setDueDate] = useState(DEFAULT_PROSPECTS[0].dueDate);
  const [specialNotes, setSpecialNotes] = useState(DEFAULT_PROSPECTS[0].specialNotes || '');
  const [deliverables, setDeliverables] = useState<DeliverableLineItem[]>(DEFAULT_PROSPECTS[0].deliverables);

  // Commercials & Taxes
  const [applyVat, setApplyVat] = useState<boolean>(true);
  const [vatRate] = useState<number>(15); // SARS 15% VAT

  // Business & Entity Details (initialized dynamically from businessSettings)
  const initialBank = parseBankDetails(
    businessSettings.bankDetails,
    businessSettings.companyName || businessSettings.senderName || 'Azura Seraph (Pty) Ltd'
  );

  const [providerCompanyName, setProviderCompanyName] = useState<string>(
    businessSettings.companyName || businessSettings.senderName || 'Azura Seraph (Pty) Ltd'
  );
  const [providerRegNo, setProviderRegNo] = useState<string>(
    businessSettings.registrationNumber || ''
  );
  const [providerVatNo, setProviderVatNo] = useState<string>(
    businessSettings.taxNumber || ''
  );
  const [providerAddress, setProviderAddress] = useState<string>(
    businessSettings.address || 'Cape Town, South Africa'
  );
  const [providerEmail, setProviderEmail] = useState<string>(
    businessSettings.billingEmail || businessSettings.email || currentUserEmail || 'billing@azuraos.app'
  );

  // Bank Wire Details (editable & auto-synced)
  const [bankName, setBankName] = useState<string>(initialBank.bankName || 'First National Bank (FNB)');
  const [accountHolder, setAccountHolder] = useState<string>(
    initialBank.accountHolder || businessSettings.companyName || businessSettings.senderName || 'Azura Seraph (Pty) Ltd'
  );
  const [accountNumber, setAccountNumber] = useState<string>(initialBank.accountNumber || '');
  const [branchCode, setBranchCode] = useState<string>(initialBank.branchCode || '');
  const [accountType, setAccountType] = useState<string>(initialBank.accountType || 'Commercial Cheque Account');

  // Keep state synchronized whenever businessSettings prop updates
  useEffect(() => {
    if (businessSettings.companyName) setProviderCompanyName(businessSettings.companyName);
    if (businessSettings.registrationNumber !== undefined) setProviderRegNo(businessSettings.registrationNumber);
    if (businessSettings.taxNumber !== undefined) setProviderVatNo(businessSettings.taxNumber);
    if (businessSettings.address) setProviderAddress(businessSettings.address);
    if (businessSettings.billingEmail || businessSettings.email) {
      setProviderEmail(businessSettings.billingEmail || businessSettings.email);
    }
    if (businessSettings.bankDetails) {
      const parsed = parseBankDetails(businessSettings.bankDetails, businessSettings.companyName || businessSettings.senderName);
      if (parsed.bankName) setBankName(parsed.bankName);
      if (parsed.accountHolder) setAccountHolder(parsed.accountHolder);
      if (parsed.accountNumber) setAccountNumber(parsed.accountNumber);
      if (parsed.branchCode) setBranchCode(parsed.branchCode);
      if (parsed.accountType) setAccountType(parsed.accountType);
    }
  }, [businessSettings]);

  const [isSavingToSettings, setIsSavingToSettings] = useState(false);
  const handleSaveToSettings = () => {
    setIsSavingToSettings(true);
    const updatedBankString = formatBankDetails({
      bankName,
      accountHolder,
      accountNumber,
      branchCode,
      accountType
    });
    const updated: BusinessSettings = {
      ...businessSettings,
      companyName: providerCompanyName,
      registrationNumber: providerRegNo,
      taxNumber: providerVatNo,
      address: providerAddress,
      email: providerEmail,
      bankDetails: updatedBankString
    };
    saveBusinessSettings(updated);
    showToast('💾 Saved company registration, tax, and banking details to Business Settings!');
    setTimeout(() => setIsSavingToSettings(false), 2000);
  };

  // UI Foldout toggles
  const [showDeliverableEditor, setShowDeliverableEditor] = useState(false);
  const [showClausesEditor, setShowClausesEditor] = useState(false);

  const proformaRef = useRef<HTMLDivElement>(null);
  const agreementRef = useRef<HTMLDivElement>(null);

  // Auto-calculated Subtotal from Deliverable items
  const sprintFeeExVat = deliverables.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
  const vatAmount = applyVat ? (sprintFeeExVat * (vatRate / 100)) : 0;
  const totalAmountDue = sprintFeeExVat + vatAmount;

  // Format Currency
  const formatZAR = (val: number) => {
    return 'R ' + val.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Sync to local storage whenever savedProspects change
  useEffect(() => {
    try {
      localStorage.setItem('azuraos_enterprise_prospects', JSON.stringify(savedProspects));
    } catch (e) {
      console.warn('Failed to persist prospects to localStorage:', e);
    }
  }, [savedProspects]);

  // Handle Loading a Prospect
  const handleSelectProspect = (id: string) => {
    setActiveProspectId(id);
    const p = savedProspects.find(x => x.id === id);
    if (!p) return;

    setClientCompany(p.clientCompany);
    setClientExecutive(p.clientExecutive);
    setClientEmail(p.clientEmail);
    setTargetDivision(p.targetDivision);
    setTargetCorridors(p.targetCorridors);
    setOperationalFocus(p.operationalFocus);
    setAnnualContractTarget(p.annualContractTarget);
    setProformaNumber(p.proformaNumber);
    setIssueDate(p.issueDate);
    setDueDate(p.dueDate);
    setSpecialNotes(p.specialNotes || '');
    setDeliverables(p.deliverables || []);
    showToast(`📂 Loaded prospect configuration for "${p.clientCompany}"`);
  };

  // Save current form state into the active prospect or update
  const handleSaveActiveProspect = () => {
    const updated = savedProspects.map(p => {
      if (p.id === activeProspectId) {
        return {
          ...p,
          clientCompany,
          clientExecutive,
          clientEmail,
          targetDivision,
          targetCorridors,
          operationalFocus,
          sprintFeeExVat,
          annualContractTarget,
          proformaNumber,
          issueDate,
          dueDate,
          specialNotes,
          deliverables
        };
      }
      return p;
    });

    setSavedProspects(updated);
    showToast(`💾 Saved changes to prospect profile "${clientCompany}"`);
  };

  // Create a new prospect from current state
  const handleSaveAsNewProspect = () => {
    const namePrompt = window.prompt('Enter a recognizable name for this new prospect profile:', clientCompany || 'New Enterprise Prospect');
    if (!namePrompt) return;

    const newId = `prospect_${Date.now()}`;
    const newProspect: EnterpriseProspect = {
      id: newId,
      name: namePrompt,
      category: 'custom',
      clientCompany,
      clientExecutive,
      clientEmail,
      targetDivision,
      targetCorridors,
      operationalFocus,
      sprintFeeExVat,
      annualContractTarget,
      proformaNumber: `AZ-PILOT-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`,
      issueDate,
      dueDate,
      specialNotes,
      deliverables
    };

    setSavedProspects(prev => [...prev, newProspect]);
    setActiveProspectId(newId);
    showToast(`✨ Created new prospect profile "${namePrompt}"`);
  };

  // Reset to Defaults
  const handleResetToDefaults = () => {
    if (window.confirm('Reset all prospect presets to default templates? Any custom unsaved changes will be cleared.')) {
      setSavedProspects(DEFAULT_PROSPECTS);
      setActiveProspectId(DEFAULT_PROSPECTS[0].id);
      const p = DEFAULT_PROSPECTS[0];
      setClientCompany(p.clientCompany);
      setClientExecutive(p.clientExecutive);
      setClientEmail(p.clientEmail);
      setTargetDivision(p.targetDivision);
      setTargetCorridors(p.targetCorridors);
      setOperationalFocus(p.operationalFocus);
      setAnnualContractTarget(p.annualContractTarget);
      setProformaNumber(p.proformaNumber);
      setIssueDate(p.issueDate);
      setDueDate(p.dueDate);
      setSpecialNotes(p.specialNotes || '');
      setDeliverables(p.deliverables);
      showToast('🔄 Reset prospect templates to defaults.');
    }
  };

  // Delete Current Prospect
  const handleDeleteCurrentProspect = () => {
    if (savedProspects.length <= 1) {
      showToast('⚠️ Cannot delete the only remaining prospect profile.');
      return;
    }
    if (window.confirm(`Delete prospect profile "${clientCompany}"?`)) {
      const filtered = savedProspects.filter(p => p.id !== activeProspectId);
      setSavedProspects(filtered);
      handleSelectProspect(filtered[0].id);
      showToast('🗑️ Prospect profile removed.');
    }
  };

  // Deliverable Line Item Handlers
  const handleUpdateDeliverable = (id: string, field: keyof DeliverableLineItem, value: any) => {
    setDeliverables(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const handleAddDeliverable = () => {
    const newItem: DeliverableLineItem = {
      id: `del_${Date.now()}`,
      title: 'New Custom Operational Deliverable',
      description: 'Scope and deliverable specification details for this milestone.',
      duration: 'Included',
      amount: 2500
    };
    setDeliverables(prev => [...prev, newItem]);
    showToast('➕ Added new deliverable line item.');
  };

  const handleRemoveDeliverable = (id: string) => {
    if (deliverables.length <= 1) {
      showToast('⚠️ Must have at least one deliverable on the proforma invoice.');
      return;
    }
    setDeliverables(prev => prev.filter(item => item.id !== id));
  };

  // Quick Distribute Sprint Fee across items
  const handleQuickDistribute = (targetTotal: number) => {
    if (deliverables.length === 0) return;
    const count = deliverables.length;
    const baseShare = Math.round(targetTotal / count);
    const updated = deliverables.map((item, idx) => ({
      ...item,
      amount: idx === count - 1 ? targetTotal - (baseShare * (count - 1)) : baseShare
    }));
    setDeliverables(updated);
    showToast(`⚡ Balanced deliverable fees to R${(targetTotal / 1000).toFixed(0)}k total.`);
  };

  // 1. PDF Download Handlers
  const handleDownloadPDF = async (type: 'proforma' | 'agreement') => {
    const targetEl = type === 'proforma' ? proformaRef.current : agreementRef.current;
    if (!targetEl) {
      showToast('⚠️ Document element not found for PDF export.');
      return;
    }

    setIsGeneratingPdf(true);
    showToast(`⏳ Rendering high-resolution ${type === 'proforma' ? 'Proforma Invoice' : 'Pilot Agreement'} PDF...`);

    // Temporarily reset preview scale for true 1:1 crisp render
    const prevScale = previewScale;
    setPreviewScale(1.0);

    try {
      // Small timeout to allow DOM to normalize
      await new Promise(res => setTimeout(res, 80));

      const canvas = await html2canvas(targetEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: documentTheme === 'dark' ? '#0E1214' : '#ffffff',
        logging: false,
        windowWidth: 1024,
        onclone: (clonedDoc) => {
          sanitizeClonedDocForPdf(clonedDoc);
          const clonedEl = targetEl.id ? clonedDoc.getElementById(targetEl.id) : null;
          if (clonedEl) {
            clonedEl.style.width = '794px';
            clonedEl.style.minWidth = '794px';
            clonedEl.style.maxWidth = '794px';
            clonedEl.style.boxSizing = 'border-box';
          }
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pageWidth = 210; // A4 width mm
      const pageHeight = 297; // A4 height mm
      const margin = 5; // 5mm margin
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - (margin * 2);

      const ratio = Math.min(availableWidth / canvas.width, availableHeight / canvas.height);
      const finalWidth = canvas.width * ratio;
      const finalHeight = canvas.height * ratio;
      const xOffset = margin + (availableWidth - finalWidth) / 2;
      const yOffset = margin + (availableHeight - finalHeight) / 2;

      if (documentTheme === 'dark') {
        pdf.setFillColor(11, 14, 15);
      } else {
        pdf.setFillColor(255, 255, 255);
      }
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight, undefined, 'FAST');

      const fileCleanName = clientCompany.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = type === 'proforma'
        ? `Proforma_Invoice_${proformaNumber}_${fileCleanName}.pdf`
        : `AzuraOS_Enterprise_Pilot_Agreement_${fileCleanName}.pdf`;

      pdf.save(filename);
      showToast(`✅ ${type === 'proforma' ? 'Proforma Invoice' : 'Pilot Agreement'} PDF downloaded successfully!`);
    } catch (err) {
      console.error('PDF export failed:', err);
      showToast('⚠️ Failed to generate PDF. Using print fallback.');
    } finally {
      setPreviewScale(prevScale);
      setIsGeneratingPdf(false);
    }
  };

  // 2. Native Print Trigger
  const handlePrint = () => {
    window.print();
  };

  // 3. Copy Quick Pitch
  const handleCopyPitch = () => {
    const pitchText = `Hi ${clientExecutive || '[Name]'},

${targetDivision} across ${targetCorridors} is the ultimate proving ground—operational margin leakages, trailing 30–60 day invoice reconciliation, multi-currency disbursements, and statutory compliance are where operating profits disappear fastest.

Especially under institutional governance frameworks (such as SEFA reporting or enterprise board oversight), maintaining strict separation between operating cash, route margins, and statutory tax buffers is essential for group accountants and audit readiness.

Here are the core framework terms for the 30-Day Guided Sandbox Pilot (${targetDivision}):

1. SCOPE & OPERATIONAL DELIVERABLES
${deliverables.map((d, i) => `• ${d.title}: ${d.description}`).join('\n')}

2. ENTERPRISE ARCHITECTURE & DATA SOVEREIGNTY
• Dedicated Cloud Tenant & Zero-Custody: Isolated cloud environment on Google Cloud. Your division’s billing data, client rates, and financial metrics are encrypted locally via AES-256-GCM with zero plaintext vendor access.
• Full Portability: Complete JSON snapshots of the division's sandbox ledgers can be exported at any point with zero vendor lock-in.

3. GUIDED SUPPORT & CASE STUDY BENCHMARK
• Four Scheduled Executive Checkpoints: Weekly 45-minute strategy reviews via Google Meet to evaluate margin variances, eliminate billing friction, and fine-tune workflow setups.
• Dedicated Founder Advisory: ${specialNotes || 'Guided remote setup and executive case study synthesis.'}

4. COMMERCIAL TERMS & 100% CONVERSION CREDIT
• Pilot Sprint Fee: A flat ${formatZAR(sprintFeeExVat)} (ex VAT) (${formatZAR(totalAmountDue)} incl. 15% VAT) covering cloud sandbox provisioning, high-touch data onboarding, weekly advisory, and executive case study synthesis.
• Committed Engineering (Non-Refundable): Because dedicated Google Cloud infrastructure and senior architecture advisory hours are committed immediately upon kickoff, all sprint fees are strictly non-refundable.
• 100% Contract Credit: The entire ${formatZAR(sprintFeeExVat)} is credited 100% directly against your annual enterprise multi-entity agreement (${annualContractTarget}) if your group transitions into a full annual rollout post-pilot.
• Zero Residual Obligation: If you decide not to proceed after 30 days, your data exports cleanly with zero continuing obligation or recurring fees.
• Strategic Channel Track: For third-party enterprise accounts originated by your firm across SADC, we establish a formal Channel Partner Agreement with 15%–20% recurring revenue share on closed annual licenses.

Please share the preferred email for yourself or the division's billing lead, and I will issue the pilot proforma invoice and onboarding calendar link.

Best regards,
Raksha Singh
Founder & CEO, ${providerCompanyName} (AzuraOS)
Google Cloud Partner | Google for Startups Cloud Program

---
Created with AzuraOS - Founder Operating System (https://azuraos.app)`;

    navigator.clipboard.writeText(pitchText);
    setCopiedPitchText(true);
    showToast('📋 Executive term sheet copied to clipboard!');
    setTimeout(() => setCopiedPitchText(false), 2500);
  };

  // 4. Copy Proforma Invoice Summary
  const handleCopyInvoiceSummary = () => {
    const regLine = providerRegNo ? `Reg: ${providerRegNo}` : 'Sole Proprietor / Emerging Entity';
    const taxLine = providerVatNo ? `Tax: ${providerVatNo}` : (applyVat ? 'Tax: Pending' : 'VAT Exempt');

    const summary = `PROFORMA INVOICE: ${proformaNumber}
Issuer: ${providerCompanyName} (${regLine} | ${taxLine})
Client: ${clientCompany}
Attention: ${clientExecutive} (${clientEmail})
Target Unit: ${targetDivision}
Corridors / Territory: ${targetCorridors}
Date: ${issueDate} | Due Date: ${dueDate}

ITEMIZED DELIVERABLES:
${deliverables.map((d, i) => `${i + 1}. ${d.title} — ${formatZAR(d.amount)} (${d.duration})`).join('\n')}

Subtotal (ex VAT): ${formatZAR(sprintFeeExVat)}
${applyVat ? `SARS VAT (${vatRate}%): ${formatZAR(vatAmount)}\nTOTAL AMOUNT DUE: ${formatZAR(totalAmountDue)}` : `TOTAL AMOUNT DUE (VAT Exempt): ${formatZAR(totalAmountDue)}`}

SPECIAL TERMS:
• Committed Engineering: Dedicated Google Cloud tenant provisioning & architecture allocation; sprint fee is strictly non-refundable upon commencement.
• 100% (${formatZAR(sprintFeeExVat)}) credited toward annual multi-entity contract upon post-pilot conversion.
• Direct bank EFT remittance details:
  Bank: ${bankName}
  Account Holder: ${accountHolder}
  Account Number: ${accountNumber || '[Provide on Invoice]'}
  Branch Code: ${branchCode || '[Provide on Invoice]'}
  Account Type: ${accountType}
  Reference: ${proformaNumber}

--------------------------------------------------------------------------------
Created with AzuraOS - Founder Operating System (https://azuraos.app)`;

    navigator.clipboard.writeText(summary);
    setCopiedInvoiceText(true);
    showToast('📋 Proforma invoice summary copied to clipboard!');
    setTimeout(() => setCopiedInvoiceText(false), 2500);
  };

  return (
    <div className="space-y-6 pt-2 border-t border-[#1F2426]">
      {/* Top Banner */}
      <div className="p-5 sm:p-6 bg-gradient-to-r from-amber-500/15 via-[#151819] to-amber-500/5 border border-amber-500/40 rounded-2xl shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-amber-500/20 border border-amber-500/50 text-amber-400 flex items-center justify-center shrink-0 shadow-inner">
              <Truck size={22} className="sm:size-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">
                  Enterprise Pilot Agreement & Proforma Generator
                </h3>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/25 text-amber-300 rounded border border-amber-500/50">
                  SUPER ADMIN
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/40">
                  REUSABLE PROSPECT ENGINE
                </span>
              </div>
              <p className="text-xs text-gray-300 mt-1 max-w-3xl leading-relaxed">
                Generate executive-grade proforma invoices, formal 30-day sandbox pilot contracts, and high-conversion term sheets for transport fleets, accelerator cohorts, and enterprise B2B prospects.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button
              onClick={handleCopyPitch}
              className="px-3 py-2 bg-[#1A1E20] hover:bg-[#252A2D] text-amber-300 hover:text-white border border-amber-500/30 hover:border-amber-500/60 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-md"
            >
              {copiedPitchText ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              <span>{copiedPitchText ? 'Copied!' : 'Copy Term Sheet'}</span>
            </button>
            <button
              onClick={() => handleDownloadPDF(activeTab === 'agreement' ? 'agreement' : 'proforma')}
              disabled={isGeneratingPdf}
              className="px-3.5 py-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Download size={14} />
              <span>{isGeneratingPdf ? 'Generating...' : `Save ${activeTab === 'agreement' ? 'Agreement' : 'Proforma'} PDF`}</span>
            </button>
          </div>
        </div>

        {/* Prospect Switcher Strip */}
        <div className="p-3.5 bg-[#0C0E0F]/90 border border-amber-500/30 rounded-xl flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <Bookmark size={16} className="text-amber-400 shrink-0" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
              <span className="text-xs font-bold text-white shrink-0">Select Active Prospect:</span>
              <div className="relative flex-1 max-w-md">
                <select
                  value={activeProspectId}
                  onChange={(e) => handleSelectProspect(e.target.value)}
                  className="w-full bg-[#151819] border border-[#2A2F31] focus:border-amber-500 text-amber-200 text-xs font-semibold px-3 py-1.5 rounded-lg outline-none cursor-pointer pr-7 appearance-none truncate"
                >
                  {savedProspects.map(p => (
                    <option key={p.id} value={p.id} className="bg-[#151819] text-white">
                      {p.name} ({formatZAR(p.sprintFeeExVat)})
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button
              type="button"
              onClick={handleSaveActiveProspect}
              className="px-3 py-1.5 bg-[#1F2426] hover:bg-[#2A2F31] border border-amber-500/40 text-amber-300 hover:text-white rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
              title="Save any modifications to current active prospect"
            >
              <Bookmark size={13} />
              <span>Save Changes</span>
            </button>

            <button
              type="button"
              onClick={handleSaveAsNewProspect}
              className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 text-amber-200 hover:text-white rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
              title="Save current details as a brand new prospect profile"
            >
              <Plus size={13} />
              <span>Save As New</span>
            </button>

            <button
              type="button"
              onClick={handleDeleteCurrentProspect}
              className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-lg transition cursor-pointer"
              title="Delete this prospect profile"
            >
              <Trash2 size={14} />
            </button>

            <button
              type="button"
              onClick={handleResetToDefaults}
              className="p-1.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-400 hover:text-white border border-[#2A2F31] rounded-lg transition cursor-pointer"
              title="Reset presets to default library"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Controls Form */}
      <div className="p-4 sm:p-6 bg-[#0C0E0F] border border-[#1F2426] rounded-2xl space-y-5 shadow-xl">
        {/* Cancelled Banner if voided via Voice Command */}
        {(() => {
          const activeProspect = savedProspects.find(p => p.id === activeProspectId);
          const isCancelled = activeProspect?.status === 'cancelled';
          if (!isCancelled) return null;
          return (
            <div className="p-3.5 bg-rose-950/40 border border-rose-500/50 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs animate-fadeIn">
              <div className="flex items-center gap-2 text-rose-200">
                <span className="font-mono font-bold uppercase tracking-wider bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded border border-rose-500/40">
                  VOIDED / CANCELLED
                </span>
                <span>
                  The R{(activeProspect?.sprintFeeExVat || 30000).toLocaleString()} sprint invoice ({proformaNumber}) for {clientCompany} was cancelled via Executive Voice Action.
                </span>
              </div>
              <span className="text-[11px] text-rose-400 font-mono shrink-0">
                Audit Trail Logged
              </span>
            </div>
          );
        })()}

        <div className="flex items-center justify-between border-b border-[#1F2426] pb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sliders size={16} className="text-amber-400" />
            <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              Prospect Commercial &amp; Client Parameters
            </h4>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDeliverableEditor(!showDeliverableEditor)}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition flex items-center gap-1 cursor-pointer ${
                showDeliverableEditor 
                  ? 'bg-teal-500/20 text-teal-300 border-teal-500/40' 
                  : 'bg-[#151819] text-gray-400 border-[#2A2F31] hover:text-white'
              }`}
            >
              <Settings2 size={13} />
              <span>{showDeliverableEditor ? 'Hide Line Items' : 'Edit Deliverables & Fees'}</span>
              {showDeliverableEditor ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Client Company Name */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
              <Building2 size={13} className="text-gray-500" /> Client Group Entity Name
            </label>
            <input
              type="text"
              value={clientCompany}
              onChange={(e) => setClientCompany(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. Imperial Logistics / B2B Holding Group"
            />
          </div>

          {/* Client Executive */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
              <Award size={13} className="text-gray-500" /> Executive Contact / Title
            </label>
            <input
              type="text"
              value={clientExecutive}
              onChange={(e) => setClientExecutive(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. Group Chief Executive Officer"
            />
          </div>

          {/* Client Email */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Executive / Billing Email</label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. ceo@logisticsgroup.co.za"
            />
          </div>

          {/* Target Division */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
              <Truck size={13} className="text-gray-500" /> Target Pilot Operating Unit
            </label>
            <input
              type="text"
              value={targetDivision}
              onChange={(e) => setTargetDivision(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. Transport & Logistics Division"
            />
          </div>

          {/* Freight Corridors / Scope Territory */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Corridors &amp; Routes / Deployment Territory</label>
            <input
              type="text"
              value={targetCorridors}
              onChange={(e) => setTargetCorridors(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. JHB – Gaborone, Durban – Harare"
            />
          </div>

          {/* Sprint Fee Indicator & Quick Balancers */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
                <DollarSign size={13} className="text-emerald-400" /> Sprint Total (ex VAT)
              </label>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {[15000, 20000, 25000, 30000, 35000].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => handleQuickDistribute(amt)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition ${
                      sprintFeeExVat === amt ? 'bg-amber-500 text-black shadow-sm' : 'bg-[#1F2426] text-gray-400 hover:text-white'
                    }`}
                    title={`Balance items to R${(amt / 1000).toFixed(0)},000`}
                  >
                    R{amt / 1000}k
                  </button>
                ))}
              </div>
            </div>
            <div className="p-2 bg-[#151819] border border-[#2A2F31] rounded-xl flex items-center justify-between">
              <span className="text-xs font-mono font-bold text-amber-300">{formatZAR(sprintFeeExVat)}</span>
              <span className="text-[10px] text-gray-400 font-mono">
                {applyVat ? `+ SARS 15% VAT (${formatZAR(vatAmount)})` : 'VAT Exempt'}
              </span>
            </div>
          </div>

          {/* Proforma Number */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Proforma Reference #</label>
            <input
              type="text"
              value={proformaNumber}
              onChange={(e) => setProformaNumber(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Issue Date */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Issue Date</label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Due Date */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Payment Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Annual Contract Anchor */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Target Annual Agreement Value</label>
            <input
              type="text"
              value={annualContractTarget}
              onChange={(e) => setAnnualContractTarget(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="e.g. R96,000 – R120,000 ex VAT"
            />
          </div>

          {/* Operational Focus */}
          <div className="sm:col-span-2 space-y-1.5">
            <label className="text-[11px] font-bold text-gray-400">Core Operational Focus &amp; Objectives</label>
            <input
              type="text"
              value={operationalFocus}
              onChange={(e) => setOperationalFocus(e.target.value)}
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
              placeholder="Operational objectives..."
            />
          </div>
        </div>

        {/* Dynamic Deliverables Line Item Editor (Expandable) */}
        {showDeliverableEditor && (
          <div className="p-4 bg-[#121516] border border-[#252A2D] rounded-xl space-y-3 pt-3">
            <div className="flex items-center justify-between border-b border-[#252A2D] pb-2">
              <div className="flex items-center gap-2">
                <CreditCard size={14} className="text-teal-400" />
                <span className="text-xs font-bold text-white uppercase tracking-wider">
                  Editable Invoice Deliverable Line Items ({deliverables.length})
                </span>
              </div>
              <button
                type="button"
                onClick={handleAddDeliverable}
                className="px-2.5 py-1 bg-teal-500 hover:bg-teal-400 text-black font-extrabold text-[11px] rounded-lg transition flex items-center gap-1 cursor-pointer"
              >
                <Plus size={12} />
                <span>Add Item</span>
              </button>
            </div>

            <div className="space-y-2.5">
              {deliverables.map((item, idx) => (
                <div key={item.id} className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="w-5 h-5 rounded-full bg-white/5 text-gray-400 text-[10px] font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateDeliverable(item.id, 'title', e.target.value)}
                        placeholder="Deliverable Title..."
                        className="w-full bg-transparent border-b border-gray-700 focus:border-teal-400 text-xs font-bold text-white outline-none py-0.5"
                      />
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1 bg-[#151819] px-2 py-1 rounded-lg border border-[#2A2F31]">
                        <span className="text-[10px] text-gray-500 font-bold">Dur:</span>
                        <input
                          type="text"
                          value={item.duration}
                          onChange={(e) => handleUpdateDeliverable(item.id, 'duration', e.target.value)}
                          className="bg-transparent text-[11px] font-mono text-gray-300 outline-none w-16 text-center"
                        />
                      </div>

                      <div className="flex items-center gap-1 bg-[#151819] px-2 py-1 rounded-lg border border-[#2A2F31]">
                        <span className="text-[10px] text-gray-500 font-bold">R</span>
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => handleUpdateDeliverable(item.id, 'amount', Number(e.target.value))}
                          className="bg-transparent text-xs font-mono font-bold text-amber-300 outline-none w-20 text-right"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveDeliverable(item.id)}
                        className="p-1 text-gray-500 hover:text-rose-400 transition cursor-pointer"
                        title="Remove deliverable item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleUpdateDeliverable(item.id, 'description', e.target.value)}
                    placeholder="Deliverable scope description..."
                    className="w-full bg-transparent text-[11px] text-gray-400 border-b border-transparent focus:border-gray-700 outline-none py-0.5"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[#252A2D] text-xs">
              <span className="text-gray-400">Calculated Subtotal (ex VAT):</span>
              <span className="font-mono font-bold text-white text-sm">{formatZAR(sprintFeeExVat)}</span>
            </div>
          </div>
        )}

        {/* Remittance, Entity, VAT & Banking Details Section */}
        <div className="p-4 bg-[#121516] border border-[#252A2D] rounded-xl space-y-3.5 text-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-[#252A2D] pb-2.5">
            <div className="flex items-center gap-2">
              <Landmark size={15} className="text-amber-400" />
              <span className="font-bold text-white text-xs uppercase tracking-wider">
                Entity Remittance, Tax ID &amp; Bank Wire Configurations
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setApplyVat(!applyVat)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition cursor-pointer ${
                  applyVat
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    : 'bg-gray-800 text-gray-400 border-gray-700'
                }`}
              >
                {applyVat ? 'SARS 15% VAT: ACTIVE' : 'VAT EXEMPT / 0%'}
              </button>

              <button
                type="button"
                onClick={handleSaveToSettings}
                disabled={isSavingToSettings}
                className="px-3 py-1 bg-[#1F2426] hover:bg-[#2A3135] text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-lg text-[10px] font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                title="Save these entity and banking details to your global Business Settings"
              >
                {isSavingToSettings ? <Check size={12} className="text-emerald-400" /> : <Save size={12} />}
                <span>{isSavingToSettings ? 'Saved to Business Settings' : 'Save to Business Settings'}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Company / Provider Name</span>
              <input
                type="text"
                value={providerCompanyName}
                onChange={(e) => setProviderCompanyName(e.target.value)}
                placeholder="e.g. Azura Seraph (Pty) Ltd"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block flex items-center justify-between">
                <span>Company Reg #</span>
                <span className="text-[9px] text-gray-500 font-normal lowercase">(optional for sole prop)</span>
              </span>
              <input
                type="text"
                value={providerRegNo}
                onChange={(e) => setProviderRegNo(e.target.value)}
                placeholder="e.g. 2024/123456/07 or blank"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-mono font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">VAT / Tax Registration #</span>
              <input
                type="text"
                value={providerVatNo}
                onChange={(e) => setProviderVatNo(e.target.value)}
                placeholder="e.g. 401928374 or Tax ID"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-mono font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Principal Business Address</span>
              <input
                type="text"
                value={providerAddress}
                onChange={(e) => setProviderAddress(e.target.value)}
                placeholder="e.g. Cape Town, South Africa"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-1 border-t border-[#1C2022]">
            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Remittance Bank</span>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g. First National Bank (FNB)"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Account Holder</span>
              <input
                type="text"
                value={accountHolder}
                onChange={(e) => setAccountHolder(e.target.value)}
                placeholder="e.g. Business or Personal Name"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Account Number</span>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="e.g. 62894120934"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-mono font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Branch Code</span>
              <input
                type="text"
                value={branchCode}
                onChange={(e) => setBranchCode(e.target.value)}
                placeholder="e.g. 250655"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-mono font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>

            <div>
              <span className="text-[10px] text-gray-500 uppercase font-bold block">Account Type</span>
              <input
                type="text"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                placeholder="e.g. Commercial Cheque"
                className="w-full bg-transparent border-b border-gray-700 py-1 font-semibold text-white focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mode Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-[#1F2426] pb-1 overflow-x-auto scrollbar-none">
        <button
          onClick={() => setActiveTab('proforma')}
          className={`px-3.5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 cursor-pointer ${
            activeTab === 'proforma'
              ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
              : 'text-gray-400 hover:text-white hover:bg-[#151819]'
          }`}
        >
          <CreditCard size={15} />
          <span>1. Proforma Invoice ({formatZAR(totalAmountDue)})</span>
        </button>

        <button
          onClick={() => setActiveTab('agreement')}
          className={`px-3.5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 cursor-pointer ${
            activeTab === 'agreement'
              ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
              : 'text-gray-400 hover:text-white hover:bg-[#151819]'
          }`}
        >
          <FileText size={15} />
          <span>2. 30-Day Sandbox Pilot Agreement</span>
        </button>

        <button
          onClick={() => setActiveTab('pitch')}
          className={`px-3.5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 cursor-pointer ${
            activeTab === 'pitch'
              ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
              : 'text-gray-400 hover:text-white hover:bg-[#151819]'
          }`}
        >
          <Sparkles size={15} />
          <span>3. Executive Term Sheet Copy</span>
        </button>

        <button
          type="button"
          onClick={() => setDocumentTheme(prev => prev === 'dark' ? 'light' : 'dark')}
          className={`ml-auto px-3 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer border ${
            documentTheme === 'dark'
              ? 'bg-teal-500/15 border-teal-500/40 text-teal-300'
              : 'bg-[#151819] border-[#2A2F31] text-gray-300 hover:text-white'
          }`}
          title="Toggle between dark glare-free view and standard white print view"
        >
          <span>{documentTheme === 'dark' ? '🌙 Dark Vault View' : '☀️ Print White View'}</span>
        </button>
      </div>

      {/* TAB 1: PROFORMA INVOICE PREVIEW */}
      {activeTab === 'proforma' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-amber-400" />
              <span className="text-xs font-mono font-bold text-gray-300 uppercase">
                Official Proforma Remittance Document
              </span>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-center">
              <button
                onClick={handleCopyInvoiceSummary}
                className="px-3 py-1.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
              >
                {copiedInvoiceText ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                <span>{copiedInvoiceText ? 'Copied' : 'Copy Text'}</span>
              </button>

              <button
                onClick={() => handleDownloadPDF('proforma')}
                disabled={isGeneratingPdf}
                className="px-3.5 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-extrabold transition flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
              >
                <Download size={13} />
                <span>Save PDF</span>
              </button>
            </div>
          </div>

          {/* Mobile Viewport & Zoom Controller */}
          <div className="bg-[#121516] border border-[#252A2D] rounded-xl p-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 text-xs">
            <div className="flex items-center gap-2 text-gray-400">
              <span className="p-1 rounded bg-amber-500/10 text-amber-400 text-sm">↔️</span>
              <div>
                <span className="font-bold text-white block text-[11px] sm:text-xs">
                  Mobile-Optimized Horizontal Scroll View
                </span>
                <span className="text-[10px] text-gray-400">
                  Swipe left or right smoothly to inspect columns, line items, and bank remittance.
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 self-end sm:self-center">
              <span className="text-[10px] uppercase font-bold text-gray-500">Zoom:</span>
              {[
                { label: '65% (Fit)', val: 0.65 },
                { label: '85%', val: 0.85 },
                { label: '100%', val: 1.0 },
              ].map(scaleOpt => (
                <button
                  key={scaleOpt.label}
                  type="button"
                  onClick={() => setPreviewScale(scaleOpt.val)}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono font-bold transition cursor-pointer ${
                    previewScale === scaleOpt.val
                      ? 'bg-amber-500 text-black shadow-sm'
                      : 'bg-[#1F2426] text-gray-300 hover:text-white'
                  }`}
                >
                  {scaleOpt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Printable Proforma Container with Touch-friendly Horizontal Scroll */}
          <div className="bg-neutral-950/80 p-3 sm:p-8 rounded-2xl border border-[#252A2D] overflow-x-auto touch-pan-x custom-scrollbar">
            <div
              style={{
                transform: previewScale < 1 ? `scale(${previewScale})` : undefined,
                transformOrigin: 'top left',
                width: previewScale < 1 ? `${(100 / previewScale).toFixed(1)}%` : undefined
              }}
              className="min-w-[700px] max-w-3xl mx-auto"
            >
              <div
                ref={proformaRef}
                id="enterprise-pilot-proforma"
                className={`rounded-xl p-8 sm:p-12 shadow-2xl w-full text-xs font-sans transition-colors ${
                  documentTheme === 'dark'
                    ? 'bg-[#0E1214] text-gray-200 border border-[#242C30]'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
                style={{
                  backgroundColor: documentTheme === 'dark' ? '#0E1214' : '#FFFFFF',
                  color: documentTheme === 'dark' ? '#E2E8F0' : '#111827'
                }}
              >
                {/* Proforma Header */}
                <div className="flex items-start justify-between border-b border-gray-200 pb-6 mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-8 h-8 rounded-lg bg-teal-600 text-white font-extrabold flex items-center justify-center text-sm"
                        style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                      >
                        {providerCompanyName.charAt(0) || 'A'}
                      </div>
                      <span className="text-xl font-bold tracking-tight text-gray-900">
                        {providerCompanyName}
                      </span>
                    </div>
                    <p className="text-gray-500 text-[11px] leading-relaxed">
                      Trading as <strong>AzuraOS™</strong> • Founder &amp; Enterprise Operating System<br />
                      Google Cloud Partner Advantage Member • Google for Startups Cloud Recipient<br />
                      {providerRegNo ? `Reg No: ${providerRegNo}` : 'Sole Proprietorship / Emerging Entity'}{providerVatNo ? ` • VAT / Tax No: ${providerVatNo}` : (applyVat ? ' • VAT Pending' : ' • VAT Exempt')}<br />
                      Inquiries: {providerEmail}
                    </p>
                  </div>

                  <div className="text-right">
                    <div
                      className="inline-block px-3 py-1 bg-amber-100 border border-amber-300 text-amber-900 font-extrabold text-xs uppercase tracking-wider rounded-md mb-2"
                      style={{ backgroundColor: '#fef3c7', borderColor: '#fcd34d', color: '#78350f' }}
                    >
                      PROFORMA INVOICE
                    </div>
                    <div className="font-mono text-sm font-bold text-gray-900">{proformaNumber}</div>
                    <div className="text-[11px] text-gray-500 mt-1">Issue Date: {issueDate}</div>
                    <div className="text-[11px] font-bold text-rose-600">Payment Due: {dueDate}</div>
                  </div>
                </div>

                {/* Billed To */}
                <div
                  className="grid grid-cols-2 gap-6 p-4 bg-gray-50 rounded-xl border border-gray-100 mb-6"
                  style={{ backgroundColor: '#f9fafb', borderColor: '#f3f4f6' }}
                >
                  <div>
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Billed To / Enterprise Client:
                    </div>
                    <div className="text-sm font-bold text-gray-900">{clientCompany}</div>
                    <div className="text-xs font-medium text-gray-700 mt-0.5">Attn: {clientExecutive}</div>
                    <div className="text-[11px] text-gray-500">{clientEmail}</div>
                    <div className="text-[11px] font-semibold text-teal-800 mt-1">
                      Target Unit: {targetDivision}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Deployment Scope &amp; Territory:
                    </div>
                    <div className="text-xs font-medium text-gray-700 leading-relaxed">
                      {targetCorridors}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                      Focus: {operationalFocus}
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full text-left border-collapse mb-6">
                  <thead>
                    <tr
                      className="border-b border-gray-300 text-[11px] uppercase tracking-wider text-gray-600 font-bold bg-gray-100"
                      style={{ backgroundColor: '#f3f4f6' }}
                    >
                      <th className="py-2.5 px-3">Item &amp; Operational Deliverable</th>
                      <th className="py-2.5 px-3 text-center">Duration</th>
                      <th className="py-2.5 px-3 text-right">Amount (ex VAT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-xs">
                    {deliverables.map((item) => (
                      <tr key={item.id}>
                        <td className="py-3 px-3">
                          <strong className="text-gray-900 font-bold block">{item.title}</strong>
                          <span className="text-[11px] text-gray-500 leading-relaxed block mt-0.5">
                            {item.description}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center font-mono whitespace-nowrap">{item.duration}</td>
                        <td className="py-3 px-3 text-right font-mono font-bold text-gray-900 whitespace-nowrap">
                          {formatZAR(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals Calculation */}
                <div className="flex justify-end mb-6">
                  <div className="w-64 space-y-2 border-t border-gray-300 pt-3 text-xs">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal (ex VAT):</span>
                      <span className="font-mono font-bold text-gray-900">{formatZAR(sprintFeeExVat)}</span>
                    </div>
                    {applyVat && (
                      <div className="flex justify-between text-gray-600">
                        <span>SARS VAT (15%):</span>
                        <span className="font-mono text-gray-900">{formatZAR(vatAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-300 pt-2">
                      <span>TOTAL AMOUNT DUE:</span>
                      <span className="font-mono text-teal-700">{formatZAR(totalAmountDue)}</span>
                    </div>
                  </div>
                </div>

                {/* Conversion Credit & Non-Refundable Commitment Seals */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <div
                    className="p-3.5 bg-emerald-50 border border-emerald-300 rounded-xl flex items-start gap-3"
                    style={{ backgroundColor: '#ecfdf5', borderColor: '#6ee7b7' }}
                  >
                    <ShieldCheck size={18} className="text-emerald-700 shrink-0 mt-0.5" />
                    <div className="text-[11px] text-emerald-950 leading-relaxed">
                      <strong className="font-bold uppercase tracking-wider block text-emerald-900">
                        100% Commercial Contract Credit Guarantee:
                      </strong>
                      The full setup fee of <strong>{formatZAR(sprintFeeExVat)}</strong> will be credited 100% directly against the client's annual enterprise multi-entity subscription ({annualContractTarget}) upon post-pilot conversion. Zero residual lock-in applies if client decides not to roll into full annual deployment.
                    </div>
                  </div>

                  <div
                    className="p-3.5 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3"
                    style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d' }}
                  >
                    <Lock size={18} className="text-amber-800 shrink-0 mt-0.5" />
                    <div className="text-[11px] text-amber-950 leading-relaxed">
                      <strong className="font-bold uppercase tracking-wider block text-amber-900">
                        Committed Engineering • Non-Refundable:
                      </strong>
                      Because the 30-Day Guided Enterprise Sprint entails immediate dedicated Google Cloud tenant provisioning, custom ledger data schema synthesis, and direct allocation of Founder &amp; Senior Architect advisory bandwidth, all sprint fees are strictly non-refundable upon commencement.
                    </div>
                  </div>
                </div>

                {/* Bank Remittance Box */}
                <div
                  className="p-4 bg-gray-100 border border-gray-300 rounded-xl text-[11px] space-y-1.5"
                  style={{ backgroundColor: '#f3f4f6', borderColor: '#d1d5db' }}
                >
                  <div className="font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                    <Landmark size={14} className="text-teal-700" /> Direct Bank Transfer / EFT Remittance Instructions:
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 font-mono text-gray-800">
                    <div><strong>Bank:</strong> {bankName}</div>
                    <div><strong>Account Holder:</strong> {accountHolder}</div>
                    <div><strong>Account Number:</strong> {accountNumber}</div>
                    <div><strong>Branch Code:</strong> {branchCode}</div>
                    <div><strong>Account Type:</strong> {accountType}</div>
                    <div><strong>Payment Reference:</strong> <span className="text-teal-700 font-extrabold">{proformaNumber}</span></div>
                  </div>
                  <p className="text-[10px] text-gray-500 italic pt-1 border-t border-gray-200 mt-2">
                    Please email proof of payment (POP) to <strong>{providerEmail}</strong> to initiate immediate sandbox provisioning and calendar scheduling.
                  </p>
                </div>

                {/* Signatures */}
                <div className="mt-8 pt-6 border-t border-gray-200 grid grid-cols-2 gap-8 text-[10px] text-gray-500">
                  <div>
                    <div className="h-8 border-b border-gray-400 mb-1 flex items-end">
                      <span className="font-serif italic text-gray-800 text-xs">Raksha Singh • {providerCompanyName}</span>
                    </div>
                    <div>Authorized Signature • {providerCompanyName}</div>
                  </div>
                  <div>
                    <div className="h-8 border-b border-gray-400 mb-1 flex items-end">
                      <span className="font-serif italic text-gray-400 text-xs">[Awaiting Acceptance]</span>
                    </div>
                    <div>Authorized Client Signatory • {clientCompany}</div>
                  </div>
                </div>

                {/* Official AzuraOS Generation Badge */}
                <div className="mt-8 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 text-[9px] text-gray-400 font-sans tracking-wide">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block"></span>
                    <span>Created with <strong>AzuraOS™</strong> • Founder Operating System</span>
                  </div>
                  <span className="font-mono text-[8px] text-gray-400">https://azuraos.app • Sovereign Commercial Infrastructure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: LEGAL PILOT AGREEMENT PREVIEW */}
      {activeTab === 'agreement' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-amber-400" />
              <span className="text-xs font-mono font-bold text-gray-300 uppercase">
                Executable 30-Day Sandbox Pilot Agreement &amp; Specification
              </span>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-center">
              <button
                onClick={() => {
                  const agreementText = document.getElementById('enterprise-pilot-agreement')?.innerText || '';
                  navigator.clipboard.writeText(agreementText);
                  setCopiedAgreementText(true);
                  showToast('📋 Agreement text copied to clipboard!');
                  setTimeout(() => setCopiedAgreementText(false), 2500);
                }}
                className="px-3 py-1.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
              >
                {copiedAgreementText ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                <span>{copiedAgreementText ? 'Copied' : 'Copy Text'}</span>
              </button>

              <button
                onClick={() => handleDownloadPDF('agreement')}
                disabled={isGeneratingPdf}
                className="px-3.5 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-extrabold transition flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
              >
                <Download size={13} />
                <span>Save Agreement PDF</span>
              </button>
            </div>
          </div>

          {/* Mobile Viewport & Zoom Controller */}
          <div className="bg-[#121516] border border-[#252A2D] rounded-xl p-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 text-xs">
            <div className="flex items-center gap-2 text-gray-400">
              <span className="p-1 rounded bg-amber-500/10 text-amber-400 text-sm">↔️</span>
              <div>
                <span className="font-bold text-white block text-[11px] sm:text-xs">
                  Mobile-Optimized Horizontal Scroll View
                </span>
                <span className="text-[10px] text-gray-400">
                  Swipe left or right smoothly to read recitals, articles, and sign-off blocks.
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 self-end sm:self-center">
              <span className="text-[10px] uppercase font-bold text-gray-500">Zoom:</span>
              {[
                { label: '65% (Fit)', val: 0.65 },
                { label: '85%', val: 0.85 },
                { label: '100%', val: 1.0 },
              ].map(scaleOpt => (
                <button
                  key={scaleOpt.label}
                  type="button"
                  onClick={() => setPreviewScale(scaleOpt.val)}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono font-bold transition cursor-pointer ${
                    previewScale === scaleOpt.val
                      ? 'bg-amber-500 text-black shadow-sm'
                      : 'bg-[#1F2426] text-gray-300 hover:text-white'
                  }`}
                >
                  {scaleOpt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Printable Agreement Container with Touch-friendly Horizontal Scroll */}
          <div className="bg-neutral-950/80 p-3 sm:p-8 rounded-2xl border border-[#252A2D] overflow-x-auto touch-pan-x custom-scrollbar">
            <div
              style={{
                transform: previewScale < 1 ? `scale(${previewScale})` : undefined,
                transformOrigin: 'top left',
                width: previewScale < 1 ? `${(100 / previewScale).toFixed(1)}%` : undefined
              }}
              className="min-w-[700px] max-w-3xl mx-auto"
            >
              <div
                ref={agreementRef}
                id="enterprise-pilot-agreement"
                className={`rounded-xl p-8 sm:p-12 shadow-2xl w-full text-xs font-sans leading-relaxed transition-colors ${
                  documentTheme === 'dark'
                    ? 'bg-[#0E1214] text-gray-200 border border-[#242C30]'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
                style={{
                  backgroundColor: documentTheme === 'dark' ? '#0E1214' : '#FFFFFF',
                  color: documentTheme === 'dark' ? '#E2E8F0' : '#111827'
                }}
              >
                {/* Title & Parties */}
                <div className="border-b border-gray-300 pb-6 mb-6 text-center">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-teal-700 font-bold mb-1">
                    COMMERCIAL PILOT FRAMEWORK
                  </div>
                  <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                    ENTERPRISE SANDBOX PILOT AGREEMENT
                  </h1>
                  <p className="text-xs text-gray-500 mt-1">
                    Operational Proof of Concept • 30-Day Guided Sprint
                  </p>
                </div>

                <div className="space-y-4 text-xs text-gray-700">
                  <p>
                    This <strong>Enterprise Sandbox Pilot Agreement</strong> (the "Agreement") is entered into as of <strong>{issueDate}</strong> by and between:
                  </p>

                  <div
                    className="p-3.5 bg-gray-50 border border-gray-200 rounded-lg space-y-1"
                    style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
                  >
                    <div><strong>1. Provider:</strong> {providerCompanyName}{providerRegNo ? ` (Reg. ${providerRegNo})` : ' (Sole Proprietorship / Emerging Entity)'}, developer of AzuraOS, with principal business address in {providerAddress}{providerVatNo ? ` (Tax/VAT: ${providerVatNo})` : ''} ("Provider").</div>
                    <div><strong>2. Participant:</strong> {clientCompany}, represented by {clientExecutive} ("Participant").</div>
                  </div>

                  <div className="pt-2">
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-wider border-b border-gray-200 pb-1 mb-2">
                      RECITALS
                    </h3>
                    <p>
                      WHEREAS Participant operates commercial operations within its <strong>{targetDivision}</strong> across {targetCorridors} and requires real-time route margin defense, fuel volatility reconciliation, automated statutory tax ring-fencing, and audit-ready reporting; and
                    </p>
                    <p className="mt-1">
                      WHEREAS Provider provides a sovereign, local-first operating layer ("AzuraOS") designed for autonomous financial protection, margin defense, and fund compliance;
                    </p>
                  </div>

                  <div className="pt-2 space-y-3">
                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-teal-800">
                        ARTICLE 1: PILOT SCOPE &amp; KEY DELIVERABLES
                      </h4>
                      <p className="mt-1">
                        During the 30-day pilot window, Provider shall provision and guide the deployment of AzuraOS for Participant's {targetDivision}, addressing:
                      </p>
                      <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                        {deliverables.map(d => (
                          <li key={d.id}>
                            <strong>{d.title}:</strong> {d.description}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-teal-800">
                        ARTICLE 2: ENTERPRISE DATA SOVEREIGNTY &amp; ZERO-CUSTODY
                      </h4>
                      <p className="mt-1">
                        AzuraOS operates on a zero-custody, local-first model. All client billing records, fuel invoices, route disbursements, and financial entries are encrypted locally on Participant's browser/hardware via <strong>AES-256-GCM</strong>. Provider retains zero unencrypted plaintext access to Participant's confidential financial metrics. Participant retains 100% data ownership and may export its complete financial ledgers in structured JSON format at any time with zero vendor lock-in.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-teal-800">
                        ARTICLE 3: GOVERNANCE &amp; OPERATIONAL CADENCE
                      </h4>
                      <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                        <li><strong>Remote Guided Onboarding:</strong> Provider shall conduct a direct remote onboarding session with Participant’s designated operations or billing lead.</li>
                        <li><strong>Weekly Review Checkpoints:</strong> Provider shall conduct four (4) scheduled 45-minute Google Meet advisory sessions to review corridor margin variances, resolve billing bottlenecks, and fine-tune operational presets.</li>
                        <li><strong>Special Logistics &amp; Travel Passthrough:</strong> {specialNotes || 'If Participant explicitly requests in-person half-day onboarding or review sessions at Participant\'s headquarters, flights, accommodation, and travel logistics shall be quoted and invoiced separately as a direct passthrough expense.'}</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-teal-800">
                        ARTICLE 4: COMMERCIAL TERMS, NON-REFUNDABLE COMMITMENT &amp; 100% CONTRACT CREDIT
                      </h4>
                      <p className="mt-1">
                        Participant agrees to remit a flat pilot sprint fee of <strong>{formatZAR(sprintFeeExVat)} (ex VAT)</strong> ({formatZAR(totalAmountDue)} incl. 15% VAT) under Proforma Invoice #{proformaNumber}.
                      </p>
                      <p className="mt-1">
                        <strong>Non-Refundable Policy:</strong> Because the 30-Day Guided Enterprise Sprint entails immediate dedicated Google Cloud infrastructure provisioning, custom ledger data schema synthesis, and direct allocation of Founder &amp; Enterprise Architecture advisory bandwidth, all sprint fees are strictly non-refundable once the sprint commences.
                      </p>
                      <p className="mt-1">
                        <strong>100% Conversion Incentive:</strong> One hundred percent (100%) of this sprint fee ({formatZAR(sprintFeeExVat)}) shall be credited directly against Participant's first-year contract value ({annualContractTarget}) if Participant elects to deploy AzuraOS on an annual enterprise multi-entity tier post-pilot.
                      </p>
                      <p className="mt-1">
                        <strong>Zero Residual Obligation:</strong> If Participant chooses not to transition to the annual enterprise tier upon conclusion of the 30-day sprint, all division data remains Participant's exclusive property with no recurring fees, cancellation penalties, or continuing obligations.
                      </p>
                      <p className="mt-1">
                        <strong>Channel Partner Track:</strong> For any third-party operating enterprises introduced by Participant or its affiliates that execute an annual AzuraOS multi-entity contract, Provider and Participant shall enter into a separate Channel Partner Agreement providing a fifteen to twenty percent (15%–20%) recurring referral commission on closed subscription revenues.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-teal-800">
                        ARTICLE 5: INTELLECTUAL PROPERTY &amp; AIR-GAPPING
                      </h4>
                      <p className="mt-1">
                        Provider retains sole and exclusive ownership of all software, source code, algorithmic engines, and trade secrets comprising AzuraOS. Participant acquires no intellectual property rights in the underlying platform.
                      </p>
                    </div>
                  </div>

                  {/* Signature Blocks */}
                  <div className="pt-6 border-t border-gray-300 mt-6 grid grid-cols-2 gap-8 text-xs">
                    <div>
                      <div className="font-bold text-gray-900 mb-1">SIGNED FOR PROVIDER:</div>
                      <div className="text-[11px] text-gray-500 mb-6">{providerCompanyName}</div>
                      <div className="border-b border-gray-400 h-6 mb-1 flex items-end">
                        <span className="font-serif italic text-gray-800 text-xs">Raksha Singh • {providerCompanyName}</span>
                      </div>
                      <div className="text-[10px] text-gray-600">Name: Raksha Singh</div>
                      <div className="text-[10px] text-gray-600">Title: Founder &amp; Chief Executive Officer</div>
                      <div className="text-[10px] text-gray-600">Date: {issueDate}</div>
                    </div>

                    <div>
                      <div className="font-bold text-gray-900 mb-1">SIGNED FOR PARTICIPANT:</div>
                      <div className="text-[11px] text-gray-500 mb-6">{clientCompany}</div>
                      <div className="border-b border-gray-400 h-6 mb-1"></div>
                      <div className="text-[10px] text-gray-600">Name: {clientExecutive}</div>
                      <div className="text-[10px] text-gray-600">Title: Authorized Executive Signatory</div>
                      <div className="text-[10px] text-gray-600">Date: ________________________</div>
                    </div>
                  </div>

                  {/* Official AzuraOS Generation Badge */}
                  <div className="mt-8 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 text-[9px] text-gray-400 font-sans tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block"></span>
                      <span>Created with <strong>AzuraOS™</strong> • Founder Operating System</span>
                    </div>
                    <span className="font-mono text-[8px] text-gray-400">https://azuraos.app • Sovereign Commercial Infrastructure</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: FAST EXECUTIVE TERM SHEET COPY */}
      {activeTab === 'pitch' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400" />
              <span className="text-xs font-mono font-bold text-gray-300 uppercase">
                Direct LinkedIn / Email Term Sheet Ready to Send
              </span>
            </div>

            <button
              onClick={handleCopyPitch}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer"
            >
              {copiedPitchText ? <Check size={14} /> : <Copy size={14} />}
              <span>{copiedPitchText ? 'Copied to Clipboard!' : 'Copy Formatted Pitch'}</span>
            </button>
          </div>

          <div className="p-6 bg-[#0C0E0F] border border-[#1F2426] rounded-2xl font-mono text-xs text-gray-300 space-y-4 leading-relaxed max-h-[500px] overflow-y-auto custom-scrollbar">
            <p className="text-gray-400">Hi {clientExecutive || '[Name]'},</p>
            <p>
              {targetDivision} across {targetCorridors} is the ultimate proving ground—operational margin leakages, trailing 30–60 day invoice reconciliation, multi-currency disbursements, and statutory compliance are where operating profits disappear fastest.
            </p>
            <p>
              Especially under institutional governance frameworks (such as SEFA reporting or enterprise board oversight), maintaining strict separation between operating cash, route margins, and statutory tax buffers is essential for group accountants and audit readiness.
            </p>

            <div className="text-amber-400 font-bold border-l-2 border-amber-500 pl-3">
              Here are the core framework terms for the 30-Day Guided Sandbox Pilot ({targetDivision}):
            </div>

            <div className="space-y-2 text-gray-300">
              <div>
                <strong className="text-white">1. OPERATIONAL FOCUS &amp; DELIVERABLES</strong><br />
                {deliverables.map((d, i) => (
                  <div key={d.id} className="mt-1">
                    • <strong>{d.title}:</strong> {d.description}
                  </div>
                ))}
              </div>

              <div className="pt-1">
                <strong className="text-white">2. ENTERPRISE ARCHITECTURE &amp; DATA SOVEREIGNTY</strong><br />
                • <strong>Zero-Custody (AES-256):</strong> Your division’s client rates, fuel slips, and invoice data are encrypted locally on the device. AzuraOS stores zero unencrypted financial records server-side.<br />
                • <strong>Full Portability:</strong> Download complete JSON snapshots of the entire division ledger at any point with zero platform lock-in.
              </div>

              <div className="pt-1">
                <strong className="text-white">3. GUIDED SUPPORT &amp; GOVERNANCE</strong><br />
                • <strong>Weekly Executive Checkpoints:</strong> Four scheduled weekly review sessions via Google Meet to evaluate route margin variances, eliminate billing friction, and fine-tune workflow setups.<br />
                • <strong>Direct Remote Onboarding:</strong> {specialNotes || 'Guided setup for your operations/billing lead.'}
              </div>

              <div className="pt-1">
                <strong className="text-white">4. COMMERCIALS &amp; CONVERSION</strong><br />
                • <strong>Pilot Sprint Fee:</strong> A flat <strong className="text-amber-300">{formatZAR(sprintFeeExVat)} (ex VAT)</strong> covering sandbox configuration, high-touch onboarding, and dedicated weekly advisory for the 30-day window.<br />
                • <strong>100% Contract Credit:</strong> The entire {formatZAR(sprintFeeExVat)} is credited directly against your annual enterprise multi-entity agreement ({annualContractTarget}) if your group transitions into a full rollout post-pilot.<br />
                • <strong>Zero Residual Lock-in:</strong> If you decide not to proceed after 30 days, your data exports cleanly with zero continuing obligation.
              </div>
            </div>

            <p className="text-teal-400 font-medium pt-2">
              If this framework works for you, please share the best email for yourself or the division's billing lead, and I’ll issue the pilot proforma invoice and onboarding calendar link to get the sandbox scheduled.
            </p>

            <div className="text-gray-400 pt-2 border-t border-gray-800 text-[11px]">
              Best regards,<br />
              <strong>Raksha Singh</strong><br />
              Founder &amp; CEO, {providerCompanyName} (AzuraOS)<br />
              Google Cloud Partner | Google for Startups Cloud Program
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const EnterprisePilotGenerator = React.memo(EnterprisePilotGeneratorComponent);

